<template>
  <div class="landing-lead-inbox space-y-4">
    <form class="card p-4 grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Filter leads" @submit.prevent="load()">
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">Source</span>
        <select v-model="filters.source_kind" data-test="filter-source" class="form-select mt-1">
          <option value="">All sources</option>
          <option value="form">Form</option>
          <option value="call">Phone call</option>
        </select>
      </label>
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">Status</span>
        <select v-model="filters.status" data-test="filter-status" class="form-select mt-1">
          <option value="">All statuses</option>
          <option v-for="status in STATUSES" :key="status" :value="status">{{ STATUS_LABELS[status] }}</option>
        </select>
      </label>
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">Site</span>
        <select v-model="filters.site_id" data-test="filter-site" class="form-select mt-1">
          <option value="">All sites</option>
          <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
        </select>
      </label>
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">Page</span>
        <select v-model="filters.page_id" data-test="filter-page" class="form-select mt-1">
          <option value="">All pages</option>
          <option v-for="page in pageOptions" :key="page.id" :value="page.id">
            {{ page.title }}{{ page.archived ? ' (archived)' : '' }}
          </option>
        </select>
      </label>
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">From</span>
        <input v-model="filters.from" data-test="filter-from" type="date" class="form-input mt-1">
      </label>
      <label class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">To</span>
        <input v-model="filters.to" data-test="filter-to" type="date" class="form-input mt-1">
      </label>
      <label class="flex items-center gap-2 text-sm self-end">
        <input v-model="filters.test_event" data-test="filter-test-event" type="checkbox">
        <span>Show test data</span>
      </label>
      <div class="flex items-end">
        <button type="button" class="btn btn-secondary" data-test="refresh" :disabled="loadState === 'loading'" @click="load()">
          Refresh
        </button>
      </div>
    </form>

    <div
      v-if="filters.test_event"
      class="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      role="note">
      Showing test data. Test leads never count as conversions and are never sent to an ad platform.
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2 text-sm" style="color: var(--text-secondary);">
      <span v-if="observedLabel">Updated {{ observedLabel }}</span>
      <span v-if="stale" class="text-amber-800" data-test="stale">
        This list may be out of date.
        <button type="button" class="underline" @click="load()">Refresh now</button>
      </span>
    </div>

    <p role="status" aria-live="polite" class="text-sm" :class="messageClass" data-test="status-message">
      {{ message ? message.text : '' }}
    </p>

    <div v-if="loadState === 'loading' && !leads.length" class="text-body" style="color: var(--text-secondary);">
      Loading leads…
    </div>
    <div v-else-if="loadState === 'unavailable'" class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
      Leads are temporarily unavailable. Your leads are safe; try again in a moment.
    </div>
    <div v-else-if="loadState === 'error'" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
      Couldn't load leads. Check your connection and refresh.
    </div>
    <div v-else-if="!leads.length" class="card p-6 text-center">
      <p class="font-medium" style="color: var(--text-primary);">
        {{ emptyTitle }}
      </p>
      <p class="text-sm mt-1" style="color: var(--text-secondary);">
        {{ emptyHint }}
      </p>
    </div>

    <table v-if="leads.length" class="w-full text-sm">
      <caption class="sr-only">
        Leads
      </caption>
      <thead class="hidden md:table-header-group text-left" style="color: var(--text-secondary);">
        <tr>
          <th scope="col" class="py-2 pr-3">
            Received
          </th>
          <th scope="col" class="py-2 pr-3">
            Source
          </th>
          <th scope="col" class="py-2 pr-3">
            Site and page
          </th>
          <th scope="col" class="py-2 pr-3">
            Call
          </th>
          <th scope="col" class="py-2 pr-3">
            Status
          </th>
          <th scope="col" class="py-2">
            <span class="sr-only">Details</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="lead in leads"
          :key="lead.id"
          class="block md:table-row card md:bg-transparent md:shadow-none mb-3 md:mb-0 p-3 md:p-0 border-b"
          :data-test="`lead-${lead.id}`">
          <td class="block md:table-cell py-1 md:py-3 pr-3 align-top">
            <span class="md:hidden font-medium">Received: </span>{{ formatTime(lead.occurred_at) }}
            <span v-if="lead.test_event" class="ml-1 text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">Test</span>
          </td>
          <td class="block md:table-cell py-1 md:py-3 pr-3 align-top">
            <span class="md:hidden font-medium">Source: </span>{{ sourceLabel(lead) }}
            <span
              v-if="lead.notification_failures > 0"
              class="block text-xs text-red-700"
              :data-test="`notification-failed-${lead.id}`">
              Notification failed
            </span>
          </td>
          <td class="block md:table-cell py-1 md:py-3 pr-3 align-top">
            <span class="md:hidden font-medium">Site and page: </span>{{ siteName(lead.site_id) }}<span v-if="lead.page_id"> · {{ pageTitle(lead.page_id) }}</span>
          </td>
          <td class="block md:table-cell py-1 md:py-3 pr-3 align-top">
            <template v-if="callOf(lead)">
              <span class="md:hidden font-medium">Call: </span>
              <span>{{ outcomeLabel(callOf(lead).outcome) }}</span>
              <span class="block text-xs" style="color: var(--text-secondary);">{{ durationLabel(callOf(lead)) }}</span>
              <span class="block text-xs" style="color: var(--text-secondary);">{{ attributionLabel(callOf(lead).attribution_quality) }}</span>
              <span v-if="settling(callOf(lead))" class="block text-xs text-amber-800">Call details still settling</span>
            </template>
          </td>
          <td class="block md:table-cell py-1 md:py-3 pr-3 align-top">
            <label class="flex items-center gap-2">
              <span class="md:hidden font-medium">Status: </span>
              <select
                class="form-select"
                :data-test="`status-${lead.id}`"
                :value="lead.status"
                :disabled="saving[lead.id] || undefined"
                :aria-label="`Status for lead received ${formatTime(lead.occurred_at)}`"
                @change="changeStatus(lead, $event.target.value)">
                <option v-for="status in STATUSES" :key="status" :value="status">{{ STATUS_LABELS[status] }}</option>
              </select>
              <span v-if="saving[lead.id]" class="text-xs" style="color: var(--text-secondary);">Saving…</span>
            </label>
          </td>
          <td class="block md:table-cell py-1 md:py-3 align-top">
            <button
              type="button"
              class="btn btn-secondary text-xs"
              :data-test="`open-lead-${lead.id}`"
              @click="openLead(lead, $event.currentTarget)">
              View details
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="nextCursor" class="text-center">
      <button type="button" class="btn btn-secondary" :disabled="loadState === 'loading'" @click="load({ more: true })">
        Load more
      </button>
    </div>

    <div
      v-if="drawer.open"
      class="fixed inset-0 bg-black/40"
      style="z-index: var(--z-modal, 1000);"
      aria-hidden="true"
      @click="closeDrawer" />
    <section
      v-if="drawer.open"
      ref="drawerEl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="landing-lead-drawer-title"
      class="fixed inset-y-0 right-0 w-full max-w-md overflow-y-auto p-5 shadow-xl"
      style="z-index: var(--z-modal, 1000); background: var(--bg-surface-elevated, #fff); color: var(--text-primary);">
      <div class="flex items-start justify-between gap-3">
        <h2 id="landing-lead-drawer-title" class="text-lg font-semibold" style="color: var(--text-primary);">
          {{ drawerLead ? sourceLabel(drawerLead) : 'Lead' }}
        </h2>
        <button ref="closeButton" type="button" class="btn btn-secondary text-xs" aria-label="Close details" @click="closeDrawer">
          Close
        </button>
      </div>

      <p v-if="drawer.state === 'loading'" class="mt-4 text-sm" style="color: var(--text-secondary);">
        Loading details…
      </p>
      <p v-else-if="drawer.state === 'error'" class="mt-4 text-sm text-red-700" role="alert">
        {{ drawer.error }}
      </p>

      <div v-else-if="drawer.detail" class="mt-4 space-y-5 text-sm">
        <p style="color: var(--text-secondary);">
          Received {{ formatTime(drawer.detail.occurred_at) }}
        </p>

        <div>
          <h3 class="font-semibold mb-1">
            Contact
          </h3>
          <dl v-if="contactRows.length" class="grid grid-cols-3 gap-x-2 gap-y-1">
            <template v-for="row in contactRows" :key="row.key">
              <dt class="col-span-1" style="color: var(--text-secondary);">
                {{ row.label }}
              </dt>
              <dd class="col-span-2 break-words">
                {{ row.value }}
              </dd>
            </template>
          </dl>
          <p v-else style="color: var(--text-secondary);">
            No contact details were captured.
          </p>
        </div>

        <div v-if="callOf(drawer.detail)">
          <h3 class="font-semibold mb-1">
            Call
          </h3>
          <p>{{ outcomeLabel(callOf(drawer.detail).outcome) }} · {{ durationLabel(callOf(drawer.detail)) }}</p>
          <p v-if="settling(callOf(drawer.detail))" class="text-amber-800">
            Call details still settling. The provider has not reported a final outcome yet.
          </p>
        </div>

        <div data-test="attribution">
          <h3 class="font-semibold mb-1">
            Attribution
          </h3>
          <p>{{ attributionLabel(drawer.detail.attribution && drawer.detail.attribution.quality) }}</p>
          <p style="color: var(--text-secondary);">
            {{ attributionHint(drawer.detail.attribution && drawer.detail.attribution.quality) }}
          </p>
        </div>

        <div data-test="export">
          <h3 class="font-semibold mb-1">
            Ad-platform conversion
          </h3>
          <p v-if="drawer.detail.export && drawer.detail.export.eligible">
            Eligible to be sent to an ad platform.
          </p>
          <p v-else>
            Not sent to an ad platform. {{ exportReason(drawer.detail.export && drawer.detail.export.reason) }}
          </p>
        </div>

        <div>
          <h3 class="font-semibold mb-1">
            Status
          </h3>
          <select
            class="form-select"
            data-test="drawer-status"
            :value="drawerLead ? drawerLead.status : drawer.detail.status"
            :disabled="(drawerLead && saving[drawerLead.id]) || undefined"
            aria-label="Lead status"
            @change="changeStatus(drawerLead || drawer.detail, $event.target.value)">
            <option v-for="status in STATUSES" :key="status" :value="status">
              {{ STATUS_LABELS[status] }}
            </option>
          </select>
        </div>

        <div>
          <h3 class="font-semibold mb-1">
            Notifications
          </h3>
          <p v-if="failedDeliveries.length" class="text-red-700" role="alert">
            The lead was saved, but a notification could not be delivered.
          </p>
          <p v-else-if="uncertainDeliveries.length" class="text-amber-800">
            The lead was saved. We could not confirm whether a notification arrived.
          </p>
          <ul v-if="notificationRows.length" class="space-y-2">
            <li v-for="row in notificationRows" :key="row.id" class="flex flex-wrap items-center justify-between gap-2">
              <span>
                {{ row.label }}: {{ deliveryLabel(row.state) }}
                <span v-if="row.last_error_code" style="color: var(--text-secondary);">({{ row.last_error_code }})</span>
              </span>
              <button
                v-if="row.retryable"
                type="button"
                class="btn btn-secondary text-xs"
                :data-test="`retry-${row.id}`"
                :disabled="retrying[row.id] || undefined"
                @click="retryDelivery(row)">
                {{ retrying[row.id] ? 'Retrying…' : 'Retry' }}
              </button>
            </li>
          </ul>
          <p v-else style="color: var(--text-secondary);">
            No notifications were sent for this lead.
          </p>
          <p v-if="drawer.retryMessage" role="status" class="mt-1" :class="drawer.retryMessage.kind === 'error' ? 'text-red-700' : 'text-green-700'">
            {{ drawer.retryMessage.text }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { apiRequest, humanize, outcomeUnknown, versionTag } from './native_api'

const props = defineProps({
  workspaceId: { type: String, required: true },
  apiBase: { type: String, default: null },
  csrfToken: { type: String, default: '' },
  initialLeads: { type: Array, default: null },
  initialMeta: { type: Object, default: null }
})

const STATUSES = ['new', 'contacted', 'qualified', 'lost']
const STATUS_LABELS = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', lost: 'Lost' }
// A connected call proves the provider bridged the call; it never proves a
// person answered, so no label may claim a human conversation.
const OUTCOME_LABELS = {
  connected: 'Connected',
  completed: 'Completed',
  no_answer: 'No answer',
  busy: 'Busy',
  failed: 'Failed',
  canceled: 'Canceled',
  voicemail: 'Voicemail',
  ringing: 'In progress',
  unknown: 'Outcome unknown'
}
const ATTRIBUTION_LABELS = {
  direct_session: 'Attributed to a page visit',
  inferred_session: 'Attribution inferred',
  source_only: 'Source only',
  ambiguous: 'Attribution uncertain',
  unknown: 'Attribution unknown'
}
const ATTRIBUTION_HINTS = {
  direct_session: 'Tied to a specific visit to your landing page.',
  inferred_session: 'Probably from a landing page visit, but not proven.',
  source_only: 'We know the traffic source, not the visit.',
  ambiguous: 'More than one visit could have produced this lead.',
  unknown: 'We could not tell which visit produced this lead.'
}
const EXPORT_REASONS = {
  unassigned: 'This account has no ad-platform connection.',
  test_event: 'Test data never counts as a conversion.',
  attribution_unavailable: 'There is no proven landing page visit to credit.',
  consent_unavailable: 'The visitor did not consent to ad measurement.',
  click_identifier_unavailable: 'No ad click was recorded for the visit.',
  eligibility_unavailable: 'Export eligibility could not be read.'
}
const SETTLING_OUTCOMES = ['ringing', 'unknown']
const DELIVERY_LABELS = { pending: 'Queued', sending: 'Sending', sent: 'Delivered', failed: 'Failed', uncertain: 'Delivery uncertain' }
const STALE_AFTER_MS = 5 * 60 * 1000

const base = computed(() => props.apiBase || `/ajax/landing_workspaces/${props.workspaceId}`)
const leads = ref(props.initialLeads ? props.initialLeads.map((lead) => ({ ...lead })) : [])
const meta = ref(props.initialMeta || { sites: [], pages: []})
const loadState = ref(props.initialLeads ? 'ready' : 'loading')
const nextCursor = ref(null)
const saving = reactive({})
const retrying = reactive({})
const message = ref(null)
const now = ref(Date.now())
const filters = reactive({ source_kind: '', status: '', site_id: '', page_id: '', from: '', to: '', test_event: false })
const drawer = reactive({ open: false, leadId: null, state: 'idle', detail: null, error: null, retryMessage: null, trigger: null })
const drawerEl = ref(null)
const closeButton = ref(null)
let clock = null
let loadSequence = 0

const sites = computed(() => meta.value.sites || [])
const pageOptions = computed(() => (meta.value.pages || []).filter((page) => !filters.site_id || page.site_id === filters.site_id))
const observedAt = computed(() => (meta.value.observed_at ? new Date(meta.value.observed_at) : null))
const observedLabel = computed(() => (observedAt.value ? observedAt.value.toLocaleTimeString() : ''))
const stale = computed(() => Boolean(observedAt.value) && now.value - observedAt.value.getTime() > STALE_AFTER_MS)
const filtered = computed(() => ['source_kind', 'status', 'site_id', 'page_id', 'from', 'to'].some((key) => filters[key]))
const emptyTitle = computed(() => {
  if (filters.test_event) return 'No test leads'
  return filtered.value ? 'No leads match these filters' : 'No leads yet'
})
const emptyHint = computed(() => (filtered.value ? 'Try widening the filters.' : 'New form submissions and phone calls from your landing pages will appear here.'))
const messageClass = computed(() => {
  if (!message.value) return ''
  return message.value.kind === 'success' ? 'text-green-700' : message.value.kind === 'conflict' ? 'text-amber-800' : 'text-red-700'
})
const drawerLead = computed(() => leads.value.find((lead) => lead.id === drawer.leadId) || null)
const contactRows = computed(() => {
  const fields = drawer.detail?.fields || {}
  const labels = { caller_number: 'Caller number', caller_name: 'Caller name' }
  return Object.keys(fields)
    .filter((key) => fields[key] !== null && fields[key] !== '')
    .map((key) => ({ key, label: labels[key] || humanize(key), value: fields[key] }))
})
const notificationRows = computed(() => {
  const delivery = drawer.detail?.delivery || {}
  const email = (delivery.email || []).map((row) => ({ ...row, label: 'Notification', kind: 'email', retryable: ['failed', 'uncertain'].includes(row.state) }))
  const crm = (delivery.crm || []).map((row) => ({ ...row, label: 'CRM delivery', kind: 'crm', retryable: false }))
  return email.concat(crm)
})
const failedDeliveries = computed(() => notificationRows.value.filter((row) => ['failed', 'dead_letter'].includes(row.state)))
const uncertainDeliveries = computed(() => notificationRows.value.filter((row) => row.state === 'uncertain'))

function callOf(lead) {
  if (!lead) return null
  if (lead.call) return lead.call
  if (lead.source_kind === 'call') {
    return { outcome: lead.outcome, connected_seconds: lead.connected_seconds, attribution_quality: lead.attribution_quality }
  }
  return null
}

function sourceLabel(lead) {
  return lead.source_kind === 'call' ? 'Phone call' : 'Form'
}

function outcomeLabel(outcome) {
  return OUTCOME_LABELS[outcome] || OUTCOME_LABELS.unknown
}

function durationLabel(call) {
  const seconds = call.connected_seconds
  if (typeof seconds !== 'number') return 'Duration not reported'
  if (seconds === 0) return 'Not connected'
  const minutes = Math.floor(seconds / 60)
  const rest = String(seconds % 60).padStart(2, '0')
  return `${minutes}:${rest} connected`
}

function attributionLabel(quality) {
  return ATTRIBUTION_LABELS[quality] || ATTRIBUTION_LABELS.unknown
}

function attributionHint(quality) {
  return ATTRIBUTION_HINTS[quality] || ATTRIBUTION_HINTS.unknown
}

function exportReason(reason) {
  return EXPORT_REASONS[reason] || (reason ? `${humanize(reason)}.` : '')
}

function deliveryLabel(state) {
  return DELIVERY_LABELS[state] || humanize(state)
}

function settling(call) {
  return SETTLING_OUTCOMES.includes(call.outcome)
}

function siteName(id) {
  return sites.value.find((site) => site.id === id)?.name || 'Site'
}

function pageTitle(id) {
  return (meta.value.pages || []).find((page) => page.id === id)?.title || 'Page'
}

function formatTime(value) {
  if (!value) return ''
  return new Date(value).toLocaleString()
}

function query(cursor) {
  const params = new URLSearchParams()
  ;['source_kind', 'status', 'site_id', 'page_id'].forEach((key) => {
    if (filters[key]) params.set(key, filters[key])
  })
  if (filters.from) params.set('from', new Date(`${filters.from}T00:00:00`).toISOString())
  if (filters.to) params.set('to', new Date(`${filters.to}T23:59:59.999`).toISOString())
  params.set('test_event', filters.test_event ? 'true' : 'false')
  params.set('limit', '50')
  if (cursor) params.set('cursor', cursor)
  return params.toString()
}

async function load({ more = false } = {}) {
  const sequence = ++loadSequence
  loadState.value = 'loading'
  const result = await apiRequest(`${base.value}/leads?${query(more ? nextCursor.value : null)}`)
  if (sequence !== loadSequence) return
  if (!result.ok) {
    loadState.value = result.status === 503 ? 'unavailable' : 'error'
    if (!more) leads.value = []
    return
  }
  leads.value = more ? leads.value.concat(result.data || []) : (result.data || [])
  meta.value = { ...meta.value, ...(result.meta || {}) }
  nextCursor.value = result.meta?.next_cursor || null
  now.value = Date.now()
  loadState.value = 'ready'
}

async function loadDetail(id) {
  drawer.state = 'loading'
  const result = await apiRequest(`${base.value}/leads/${id}`)
  if (drawer.leadId !== id) return
  if (!result.ok) {
    drawer.state = 'error'
    drawer.error = result.status === 404 ? 'This lead is no longer available.' : "Couldn't load this lead. Try again."
    return
  }
  drawer.detail = result.data
  drawer.state = 'ready'
}

async function openLead(lead, trigger) {
  drawer.open = true
  drawer.leadId = lead.id
  drawer.detail = null
  drawer.retryMessage = null
  drawer.trigger = trigger || null
  await nextTick()
  closeButton.value?.focus()
  await loadDetail(lead.id)
}

function closeDrawer() {
  const trigger = drawer.trigger
  drawer.open = false
  drawer.leadId = null
  drawer.detail = null
  drawer.trigger = null
  if (trigger && typeof trigger.focus === 'function') nextTick(() => trigger.focus())
}

async function changeStatus(lead, status) {
  if (!lead || saving[lead.id] || status === lead.status) return
  const previous = lead.status
  const target = leads.value.find((row) => row.id === lead.id) || lead
  saving[lead.id] = true
  message.value = null
  target.status = status
  const result = await apiRequest(`${base.value}/leads/${lead.id}`, {
    method: 'PATCH', body: { status }, etag: versionTag(target.version), csrfToken: props.csrfToken
  })
  try {
    if (result.ok && result.data) {
      Object.assign(target, { status: result.data.status, version: result.data.version })
      if (drawer.detail && drawer.detail.id === lead.id) Object.assign(drawer.detail, { status: result.data.status, version: result.data.version })
      message.value = { kind: 'success', text: 'Status saved.' }
      return
    }
    target.status = previous
    if (result.status === 412) {
      message.value = { kind: 'conflict', text: 'This lead changed since you loaded it. We reloaded the latest version; review it and try again.' }
      await load()
      if (drawer.open && drawer.leadId === lead.id) await loadDetail(lead.id)
    } else if (outcomeUnknown(result) || result.status === 409) {
      // The save may have reached the server: show what it holds, never "failed".
      message.value = { kind: 'conflict', text: "We couldn't confirm the save, so we reloaded the lead. Check its status before changing it again." }
      await load()
      if (drawer.open && drawer.leadId === lead.id) await loadDetail(lead.id)
    } else if (result.status === 404) {
      message.value = { kind: 'error', text: 'This lead is no longer available.' }
    } else {
      message.value = { kind: 'error', text: "Couldn't save the status. Nothing was changed; try again." }
    }
  } finally {
    saving[lead.id] = false
  }
}

async function retryDelivery(row) {
  if (retrying[row.id]) return
  retrying[row.id] = true
  drawer.retryMessage = null
  const result = await apiRequest(`${base.value}/lead_email_deliveries/${row.id}/retry`, {
    method: 'POST', body: {}, etag: versionTag(row.version), csrfToken: props.csrfToken
  })
  retrying[row.id] = false
  if (result.ok) {
    drawer.retryMessage = { kind: 'success', text: 'Notification queued to send again.' }
  } else if (outcomeUnknown(result) || result.status === 409) {
    drawer.retryMessage = { kind: 'error', text: "We couldn't confirm the retry, so we reloaded the notification state." }
  } else if (result.status === 412) {
    drawer.retryMessage = { kind: 'error', text: 'This notification changed since you loaded it. We reloaded it; try again if it still failed.' }
  } else {
    drawer.retryMessage = { kind: 'error', text: `Couldn't retry the notification (${humanize(result.error?.code) || 'request failed'}).` }
  }
  if (drawer.leadId) await loadDetail(drawer.leadId)
}

watch(filters, () => {
  if (filters.page_id && !pageOptions.value.some((page) => page.id === filters.page_id)) filters.page_id = ''
  load()
}, { deep: true })

// The drawer is modal: Escape closes it wherever focus is, and Tab cycles
// through the drawer's own controls only.
function onDocumentKeydown(event) {
  if (!drawer.open) return
  if (event.key === 'Escape') {
    event.preventDefault()
    closeDrawer()
    return
  }
  if (event.key !== 'Tab' || !drawerEl.value) return
  const focusables = [...drawerEl.value.querySelectorAll('a[href], button, select, input, textarea, [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.disabled)
  if (!focusables.length) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const inside = drawerEl.value.contains(document.activeElement)
  if (event.shiftKey && (!inside || document.activeElement === first)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
    event.preventDefault()
    first.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown)
  clock = setInterval(() => { now.value = Date.now() }, 30000)
  if (!props.initialLeads) load()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocumentKeydown)
  if (clock) clearInterval(clock)
})
</script>
